import type { Agent, HealthStatus, Model } from "./manifest"

/** Display helpers for the runner console. Pure, so they're unit-tested. */

// ---------------------------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------------------------

/** Compact duration: "45 s", "12 min", "2 h 14 min", "3 d 4 h". */
export const formatDuration = (ms: number): string => {
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return "<1 min"
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rest = minutes % 60
    return rest ? `${hours} h ${rest} min` : `${hours} h`
  }
  const days = Math.floor(hours / 24)
  const rest = hours % 24
  return rest ? `${days} d ${rest} h` : `${days} d`
}

/** "resets in 2 h 14 min"; "resets now" once the time has passed (the next probe brings fresh numbers). */
export const formatResetsIn = (resetsAt: string | null, now: number = Date.now()): string | null => {
  if (!resetsAt) return null
  const at = Date.parse(resetsAt)
  if (Number.isNaN(at)) return null
  const ms = at - now
  if (ms <= 0) return "resets now"
  return `resets in ${formatDuration(ms)}`
}

// ---------------------------------------------------------------------------------------------
// Privacy
// ---------------------------------------------------------------------------------------------

/**
 * Runners send account labels already masked ("j…@gmail.com"). If one ever arrives unmasked, mask it
 * here too, so a full address is never on screen: keep the first character of the local part and the
 * domain. Labels that are not email addresses (an org name, "API key") pass through.
 */
export const maskAccountLabel = (label: string | null): string | null => {
  if (!label) return null
  const trimmed = label.trim()
  const at = trimmed.lastIndexOf("@")
  if (at <= 0) return trimmed
  const local = trimmed.slice(0, at)
  const domain = trimmed.slice(at + 1)
  if (!domain) return trimmed
  // Idempotent: "j…@gmail.com" maps to itself.
  return `${local[0]}…@${domain}`
}

// ---------------------------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------------------------

export interface CapabilityGroup {
  readonly id: string
  readonly label: string
  readonly items: ReadonlyArray<{ readonly token: string; readonly label: string }>
}

const groupOrder = ["platform", "agents", "computer-use", "tools", "other"] as const
const groupLabels: Record<(typeof groupOrder)[number], string> = {
  platform: "Platform",
  agents: "Agents",
  "computer-use": "Computer use",
  tools: "Tools",
  other: "Other",
}

const osLabels: Record<string, string> = {
  macos: "macOS",
  darwin: "macOS",
  linux: "Linux",
  windows: "Windows",
}
const agentLabels: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
  antigravity: "Antigravity",
}

/**
 * Groups capability tokens for display: `os:`/`arch:` → Platform, `agent:` → Agents,
 * `computer-use:` → Computer use, bare tokens (`xcode`, `docker`, `gh`) → Tools, any other
 * `prefix:value` → Other. Order inside a group follows the runner; duplicates are dropped.
 */
export const groupCapabilities = (capabilities: ReadonlyArray<string>): ReadonlyArray<CapabilityGroup> => {
  const groups = new Map<(typeof groupOrder)[number], Array<{ token: string; label: string }>>()
  const seen = new Set<string>()
  for (const token of capabilities) {
    if (seen.has(token)) continue
    seen.add(token)
    const colon = token.indexOf(":")
    const prefix = colon > 0 ? token.slice(0, colon) : null
    const value = colon > 0 ? token.slice(colon + 1) : token
    let group: (typeof groupOrder)[number]
    let label: string
    if (prefix === "os") {
      group = "platform"
      label = osLabels[value] ?? value
    } else if (prefix === "arch") {
      group = "platform"
      label = value
    } else if (prefix === "agent") {
      group = "agents"
      label = agentLabels[value] ?? value
    } else if (prefix === "computer-use") {
      group = "computer-use"
      label = agentLabels[value] ?? value
    } else if (prefix === null) {
      group = "tools"
      label = value
    } else {
      group = "other"
      label = token
    }
    const list = groups.get(group) ?? []
    list.push({ token, label })
    groups.set(group, list)
  }
  return groupOrder.flatMap((id) => {
    const items = groups.get(id)
    return items ? [{ id, label: groupLabels[id], items }] : []
  })
}

// ---------------------------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------------------------

const parseVersion = (version: string) => {
  const match = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?/.exec(version.trim())
  if (!match) return null
  return {
    core: [Number(match[1]), Number(match[2]), Number(match[3])] as const,
    pre: match[4] ? match[4].split(".") : [],
  }
}

/** Semver order: -1, 0, 1. Unparseable versions compare as equal (never offer an "update" we can't reason about). */
export const compareVersions = (a: string, b: string): number => {
  const va = parseVersion(a)
  const vb = parseVersion(b)
  if (!va || !vb) return 0
  for (let i = 0; i < 3; i++) {
    const d = (va.core[i] ?? 0) - (vb.core[i] ?? 0)
    if (d !== 0) return Math.sign(d)
  }
  // A release outranks its pre-releases: 1.0.0 > 1.0.0-rc.1.
  if (va.pre.length === 0 || vb.pre.length === 0) return Math.sign(vb.pre.length - va.pre.length)
  for (let i = 0; i < Math.max(va.pre.length, vb.pre.length); i++) {
    const pa = va.pre[i]
    const pb = vb.pre[i]
    if (pa === undefined) return -1
    if (pb === undefined) return 1
    const na = /^\d+$/.test(pa) ? Number(pa) : null
    const nb = /^\d+$/.test(pb) ? Number(pb) : null
    if (na !== null && nb !== null && na !== nb) return Math.sign(na - nb)
    if (na !== null && nb === null) return -1
    if (na === null && nb !== null) return 1
    if (pa !== pb) return pa < pb ? -1 : 1
  }
  return 0
}

/** The newer release, or null when the runner is current (or either version is unreadable). */
export const availableUpdate = (current: string, latest: string | null | undefined): string | null =>
  latest && compareVersions(latest, current) > 0 ? latest : null

/** A requested update the runner hasn't reached after this long (while online) can be retried. */
export const UPDATE_STALL_MS = 10 * 60_000

/** The runner's last `update.status`, as the hub relays it (PROVISIONAL: not sent by the hub yet). */
export interface UpdateStatusInfo {
  readonly state: "waiting_idle" | "downloading" | "installed" | "up_to_date" | "failed" | "unsupported"
  readonly version: string | null
  readonly message: string | null
  readonly at: number | null
}

export type UpdateView =
  | { readonly kind: "none" }
  | { readonly kind: "available"; readonly version: string }
  | { readonly kind: "updating"; readonly version: string; readonly step: string | null }
  /** Requested, but the runner didn't get there: show why and offer a retry when `retry`. */
  | { readonly kind: "stuck"; readonly version: string; readonly reason: string; readonly retry: boolean }

const updateSteps: Partial<Record<UpdateStatusInfo["state"], string>> = {
  waiting_idle: "waiting until it's idle",
  downloading: "downloading",
  installed: "installed, restarting",
}

/**
 * What the card shows about updates. The hub clears `pending` only when the runner reaches the
 * version (or after a day), so a failed or refused update must be recognised here: from the
 * runner's `update.status` when the hub relays it, otherwise by the request going stale while the
 * runner is online at an older version.
 */
export const updateView = (o: {
  readonly current: string
  readonly available: string | null
  readonly pending: { readonly version: string; readonly requestedAt: number | string | null } | null
  readonly status: UpdateStatusInfo | null
  readonly online: boolean
  readonly now: number
}): UpdateView => {
  const pending = o.pending && compareVersions(o.current, o.pending.version) < 0 ? o.pending : null
  if (!pending) return o.available ? { kind: "available", version: o.available } : { kind: "none" }
  const requestedAt = toMillis(pending.requestedAt)
  // A status older than this request belongs to an earlier attempt.
  const status =
    o.status && (requestedAt === null || o.status.at === null || o.status.at >= requestedAt) ? o.status : null
  if (status?.state === "failed")
    return {
      kind: "stuck",
      version: pending.version,
      reason: status.message ?? "The update failed.",
      retry: true,
    }
  if (status?.state === "unsupported")
    return {
      kind: "stuck",
      version: pending.version,
      reason: status.message ?? "This runner can't update itself.",
      retry: false,
    }
  if (status?.state === "up_to_date")
    return {
      kind: "stuck",
      version: pending.version,
      reason: "The runner found no newer build for its platform.",
      retry: true,
    }
  const stalled = o.online && requestedAt !== null && o.now - requestedAt > UPDATE_STALL_MS
  if (stalled && (status === null || status.state === "downloading"))
    return {
      kind: "stuck",
      version: pending.version,
      reason: `The runner is still on v${o.current}.`,
      retry: true,
    }
  return {
    kind: "updating",
    version: pending.version,
    step: status ? (updateSteps[status.state] ?? null) : null,
  }
}

const toMillis = (value: number | string | null): number | null => {
  if (value === null) return null
  const ms = typeof value === "number" ? value : Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

// ---------------------------------------------------------------------------------------------
// Profiles, models, usage
// ---------------------------------------------------------------------------------------------

const cliNames: Record<Agent, string> = {
  claude: "Claude Code",
  codex: "Codex",
  antigravity: "Antigravity",
  muse: "Muse Code",
}

/** "Claude Code 2.1.293", "Codex 0.161.0", or just the product name when the version is unknown. */
export const cliLabel = (agent: Agent, version: string | null): string =>
  version ? `${cliNames[agent]} ${version.replace(/^v/, "")}` : cliNames[agent]

/** "max" → "Max", "pro" → "Pro", "chatgpt_plus" → "Chatgpt plus"; keeps already-cased labels. */
export const planLabel = (plan: string | null): string | null => {
  if (!plan) return null
  const words = plan.replace(/[_-]+/g, " ").trim()
  return words === words.toLowerCase() ? words.charAt(0).toUpperCase() + words.slice(1) : words
}

/** 200000 → "200k", 1000000 → "1M". */
export const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`
  if (n >= 1000) return `${Math.round(n / 1000)}k`
  return String(n)
}

/** The model a profile uses by default: `defaultModel`, else the model flagged `isDefault`. */
export const defaultModelOf = (profile: {
  readonly defaultModel: string | null
  readonly models: ReadonlyArray<Model>
}): Model | { readonly id: string; readonly label: null } | null => {
  if (profile.defaultModel) {
    return (
      profile.models.find((m) => m.id === profile.defaultModel) ?? { id: profile.defaultModel, label: null }
    )
  }
  return profile.models.find((m) => m.isDefault) ?? null
}

export type UsageLevel = "ok" | "warn" | "high"
/** Bar color bands: under 70 % fine, 70–89 % watch it, 90 %+ about to hit the limit. */
export const usageLevel = (usedPercent: number): UsageLevel =>
  usedPercent >= 90 ? "high" : usedPercent >= 70 ? "warn" : "ok"

export const healthLabels: Record<HealthStatus, string> = {
  ok: "healthy",
  logged_out: "signed out",
  error: "error",
  unknown: "not checked",
}

/** 64_000_000 → "61 MB". */
export const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${Math.round(n / (1024 * 1024))} MB`
}

const platformLabels: Record<string, string> = {
  darwin: "macOS",
  macos: "macOS",
  linux: "Linux",
  windows: "Windows",
  win32: "Windows",
}
/** "darwin" → "macOS". */
export const osLabel = (os: string): string => platformLabels[os] ?? os

// ---------------------------------------------------------------------------------------------
// Install
// ---------------------------------------------------------------------------------------------

export const PRODUCTION_ORIGIN = "https://app.minga.run"

export type InstallOs = "macos" | "linux" | "windows"

/** The one-liner for this server. The script downloads from the same origin it was served from. */
export const installCommand = (origin: string): string => `curl -fsSL ${origin}/install.sh | sh`

/**
 * Decision 0166 §5: the install script's pin example, with the version published now (never a stale literal), else a
 * placeholder while none is known.
 */
export const pinVersionExample = (latest: string | null | undefined): string =>
  `MINGA_VERSION=${latest?.trim() || "<version>"}`

/** `minga runner login`, plus `--server` when this isn't the production app (local dev, previews). */
export const loginCommand = (origin: string): string =>
  origin === PRODUCTION_ORIGIN ? "minga runner login" : `minga runner login --server ${origin}`

const assetOs: Record<InstallOs, string> = { macos: "darwin", linux: "linux", windows: "windows" }
const archLabels: Record<string, string> = {
  arm64: "Apple silicon (arm64)",
  x64: "Intel (x64)",
}
const linuxArchLabels: Record<string, string> = { x64: "x86-64", arm64: "ARM64 (aarch64)" }

/** Release assets for one OS, as `{ key: "darwin-arm64", label: "Apple silicon (arm64)", … }`, arm64 first. */
export const assetsForOs = <A>(
  assets: Readonly<Record<string, A>>,
  os: InstallOs,
): ReadonlyArray<{
  readonly key: string
  readonly arch: string
  readonly label: string
  readonly asset: A
}> =>
  Object.entries(assets)
    .filter(([key]) => key.startsWith(`${assetOs[os]}-`))
    .map(([key, asset]) => {
      const arch = key.slice(assetOs[os].length + 1)
      const label = (os === "macos" ? archLabels[arch] : linuxArchLabels[arch]) ?? arch
      return { key, arch, label, asset }
    })
    .sort((a, b) => Number(b.arch === "arm64") - Number(a.arch === "arm64") || a.key.localeCompare(b.key))

/** Best guess of the visitor's OS for the default install tab. */
export const guessInstallOs = (userAgent: string): InstallOs =>
  /windows/i.test(userAgent)
    ? "windows"
    : /linux|x11|cros/i.test(userAgent) && !/android/i.test(userAgent)
      ? "linux"
      : "macos"
